import he from 'he';

type InertNode = {
  nodeType: number;
  nodeName: string;
  textContent: string | null;
  childNodes: ArrayLike<InertNode>;
};

type InertHtmlParser = {
  parseFromString: (html: string, type: 'text/html') => { body: InertNode };
};
declare const DOMParser: (new () => InertHtmlParser) | undefined;

type StripHtmlOptions = { blockSpacing?: boolean; onlyIfHtml?: boolean };

const TEXT_NODE = 3;

const BLOCK_TAGS = [
  'p',
  'div',
  'li',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
];
const INLINE_TAGS = ['br', 'hr', 'span', 'strong', 'em', 'b', 'i', 'u', 's', 'a', 'code', 'mark', 'sub', 'sup', 'img'];

const BLOCK_ELEMENTS = new Set([...BLOCK_TAGS, 'br'].map((tag) => tag.toUpperCase()));
const BLOCK_BOUNDARY = new RegExp(`</(?:${BLOCK_TAGS.join('|')})\\s*>|<br\\b[^>]*>`, 'gi');
const BLOCK_ENTRY = new RegExp(`<(?:${BLOCK_TAGS.join('|')})(?=[\\s/>])`, 'gi');

const HTML_TAG_RE = new RegExp(`</?(?:${[...BLOCK_TAGS, ...INLINE_TAGS].join('|')})(?=[\\s/>])[^>]*>`, 'i');
const HTML_ENTITY_RE =
  /&(?:#\d+|#x[0-9a-f]+|amp|lt|gt|quot|apos|nbsp|copy|reg|trade|mdash|ndash|hellip|lsquo|rsquo|ldquo|rdquo|bull|deg);/i;

const nbspToSpace = (text: string): string => text.replace(/ /g, ' ');

// gloss
const decodeText = (text: string): string => nbspToSpace(he.decode(text));

// gloss
const spacedText = (node: InertNode): string => {
  if (node.nodeType === TEXT_NODE) return nbspToSpace(node.textContent ?? '');
  let text = '';
  for (const child of Array.from(node.childNodes)) {
    if (BLOCK_ELEMENTS.has(child.nodeName)) text += ' ';
    text += spacedText(child);
    if (BLOCK_ELEMENTS.has(child.nodeName)) text += ' ';
  }
  return text;
};

// gloss
export const stripHtml = (html?: string | null, options?: StripHtmlOptions): string => {
  if (!html) return '';
  if (options?.onlyIfHtml && !HTML_TAG_RE.test(html)) {
    if (!HTML_ENTITY_RE.test(html)) return html.trim();
    if (typeof DOMParser !== 'undefined') {
      const escaped = html.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const { body } = new DOMParser().parseFromString(escaped, 'text/html');
      return nbspToSpace(body.textContent || '').trim();
    }
    return decodeText(html).trim();
  }
  const blockSpacing = options?.blockSpacing === true;
  if (typeof DOMParser !== 'undefined') {
    const { body } = new DOMParser().parseFromString(html, 'text/html');
    if (blockSpacing) return spacedText(body).replace(/\s+/g, ' ').trim();
    return nbspToSpace(body.textContent || '');
  }
  const source = blockSpacing ? html.replace(BLOCK_BOUNDARY, '$& ').replace(BLOCK_ENTRY, ' $&') : html;
  const text = decodeText(source.replace(/<\/?[a-zA-Z][^>]*>/g, ''));
  return blockSpacing ? text.replace(/\s+/g, ' ').trim() : text.trim();
};
